/**
 * The cover (01), a chapter opener (02), the one page (38) and the brief (41).
 */
import type { ReactNode } from "react";
import type { StrategySource } from "@/types/strategyDocument.type";
import {
  Eyebrow, Foot, Headline, Key, Picture, Slide, SourceLine, V,
  capFirst, countryName, dayLong, dayLongYear, figure, joinAnd, monthYear, num, platformName, sentence, slideText,
  type TemplateContext, FigureOr
} from "./kit";

/* ── 01 Cover ─────────────────────────────────────────────────────────── */

export function CoverSlide({ ctx }: { ctx: TemplateContext }) {
  return (
    <Slide ink className="sv-cover">
      <Picture ctx={ctx} slot="cover" label="The cover picture" className="sv-cover-pic" />
      <div className="sv-cover-veil" aria-hidden />
      <p className="sv-cover-kind">Social strategy</p>
      <V as="p" className="sv-cover-brand" dir="auto">{ctx.brand}</V>
      {ctx.periodStart && ctx.periodEnd ? (
        <p className="sv-cover-period"><V>{monthYear(ctx.periodStart)}</V> to <V>{monthYear(ctx.periodEnd)}</V></p>
      ) : null}
    </Slide>
  );
}

/* ── 02 Chapter opener ────────────────────────────────────────────────── */

/** The plate's title column holds about 13 characters a line at 104px; a longer line steps the size down. */
const OPENER_LINE_CHARS = 13;

export function OpenerSlide({ ctx }: { ctx: TemplateContext }) {
  const spec = ctx.chapter!;
  const of = ctx.chapterOf;
  // The strategy's own title and question (slide_text "opener:<chapter>"), else the chapter's defaults.
  const t = slideText(ctx);
  const written = t.headline?.replace(/\\+n/g, "\n").replace(/\*/g, "").split("\n").map((line) => line.trim()).filter(Boolean).slice(0, 2);
  const title = written?.length ? written : [...spec.title];
  const question = t.reading?.trim() || spec.question;
  const longest = Math.max(...title.map((line) => line.length));
  const size = longest > OPENER_LINE_CHARS ? Math.max(64, Math.floor((104 * OPENER_LINE_CHARS) / longest)) : null;
  return (
    <Slide ink className="sv-opener">
      <div className="sv-opener-copy">
        <span className="sv-push" />
        {of ? <p className="sv-opener-number">Chapter {String(of.number).padStart(2, "0")} of {of.total}</p> : null}
        <div className="sv-opener-title" style={size ? { fontSize: size, lineHeight: `${Math.round(size * 1.115)}px` } : undefined}>
          {title.map((line, i) => <V as="p" key={i}>{line}</V>)}
        </div>
        <V as="p" className="sv-opener-question">{question}</V>
        <Foot ctx={ctx} label={null} page={of ? `${ctx.page}` : ctx.page} />
      </div>
      <Picture ctx={ctx} slot={`chapter:${spec.number}`} label={`The picture for ${spec.title.join(" ")}`} className="sv-opener-pic" />
    </Slide>
  );
}

/* ── The two-column frame (05, 38): copy on paper, two figures on ink ──── */

export interface PanelStat {
  figure: ReactNode;
  label: ReactNode;
  statement?: ReactNode;
  source?: StrategySource | null;
  sourceText?: ReactNode;
}

export function StatsPanel({ stats }: { stats: PanelStat[] }) {
  return (
    <div className="sv-stats">
      {stats.map((stat, i) => (
        <div key={i} className="sv-stat">
          <p className="sv-stat-figure">{stat.figure}</p>
          <p className="sv-stat-label">{stat.label}</p>
          {stat.statement ? <p className="sv-stat-statement">{stat.statement}</p> : null}
          <SourceLine source={stat.source}>{stat.sourceText}</SourceLine>
        </div>
      ))}
    </div>
  );
}

/* ── 38 The strategy on one page ──────────────────────────────────────── */

function onePageLines(ctx: TemplateContext): Array<{ label: string; text: string | null }> {
  const { doc } = ctx;
  const s = doc.summary ?? {};
  const people = doc.audience?.people ?? [];
  const markets = (doc.brief?.markets ?? []).map((m) => m.name || countryName(m.country));
  const goals = doc.goals?.list ?? [];
  const pillars = doc.content?.pillars ?? [];
  const bigMoments = (doc.culture?.cultural_moments ?? []).filter((m) => m.is_big).map((m) => m.moment);
  const moments = bigMoments.length ? bigMoments : (doc.year?.big_moments ?? []).map((m) => m.name);
  const firstSentence = (text: string) => sentence(text.split(/(?<=[.!?])\s/)[0] ?? text);
  return [
    { label: "Objective", text: s.objective ?? doc.brief?.business_objective?.line ?? null },
    { label: "The idea", text: doc.insight?.idea?.line ? sentence(doc.insight.idea.line) : null },
    { label: "Audience", text: s.audience ?? (people.length ? sentence(`${joinAnd(people.map((p) => p.name))}${markets.length ? `, in ${joinAnd(markets)}` : ""}`) : null) },
    { label: "Platforms", text: s.platforms ?? ((doc.platforms?.list ?? []).length ? (doc.platforms!.list.map((p) => `${platformName(p.platform)}: ${firstSentence(p.job).replace(/^\p{Lu}/u, (c) => c.toLowerCase())}`).join(" ")) : null) },
    { label: "Goals", text: s.goals ?? (goals.length ? goals.map((g) => sentence(`${figure(g.target.value, g.target.unit, { lower: true })} ${g.short ?? g.name ?? ""}`.trim())).join(" ") : null) },
    { label: "Pillars", text: s.pillars ?? (pillars.length ? sentence(pillars.map((p) => `${p.name} ${p.share_pct}%`).join(", ")) : null) },
    { label: "Key moments", text: s.key_moments ?? (moments.length ? sentence(joinAnd(moments)) : null) },
    { label: "Not for us", text: s.not_for_us ?? ((doc.insight?.not_for_us ?? []).length ? doc.insight!.not_for_us.map(sentence).join(" ") : null) },
  ];
}

export function OnePageSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const t = slideText(ctx);
  const lines = onePageLines(ctx);
  const columns = [lines.slice(0, 4), lines.slice(4)];
  const goals = doc.goals?.list ?? [];
  const watch = goals.find((g) => g.id === doc.goals?.watch_most) ?? goals[0];
  const volume = doc.content?.volume;
  const approved = doc.goals?.checks?.approved_by_client_at;
  const stats: PanelStat[] = [];
  if (watch) {
    const platforms = watch.platforms.map(platformName);
    stats.push({
      figure: <V>{figure(watch.target.value, watch.target.unit, { lower: true })}</V>,
      label: <><V>{watch.name ?? watch.measure_id}</V> by <V>{monthYear(watch.target.date)}</V></>,
      statement: watch.starting_point ? <>From <V>{num(watch.starting_point, { lower: true })}</V> today{platforms.length ? <>, <V>{joinAnd(platforms)}</V>{platforms.length > 1 ? " together" : ""}</> : null}.</> : null,
      sourceText: approved ? <>Set with {ctx.brand}, {dayLong(approved)}</> : "Chapter 8",
    });
  }
  if (volume) {
    const per = [...volume.per_platform].sort((a, b) => b.posts_per_month - a.posts_per_month);
    stats.push({
      figure: <V>{volume.monthly_total}</V>,
      label: "Posts a month",
      statement: per.length ? <V>{sentence(per.map((p) => `${p.posts_per_month} on ${platformName(p.platform)}`).join(", "))}</V> : null,
      sourceText: "Chapter 10",
    });
  }
  return (
    <Slide className="sv-two">
      <div className="sv-col sv-two-copy">
        <Eyebrow ctx={ctx} label="On one page" />
        <Headline text={t.headline} fallback={"What we commit to.\n*On one page.*"} />
        <p className="sv-sub">{t.reading ? <V>{t.reading}</V> : "The whole strategy in eight lines. The chapters that follow show the work behind each one."}</p>
        <span className="sv-push" />
        <div className="sv-onepage-cols">
          {columns.map((col, c) => (
            <div key={c} className="sv-onepage-col">
              {col.map((line) => (
                <div key={line.label} className="sv-onepage-line">
                  <Key>{line.label}</Key>
                  {line.text ? <V as="p">{line.text}</V> : <p className="sv-open">Open</p>}
                </div>
              ))}
            </div>
          ))}
        </div>
        <Foot ctx={ctx} label="On one page" />
      </div>
      <StatsPanel stats={stats} />
    </Slide>
  );
}

/* ── 41 The brief ─────────────────────────────────────────────────────── */

function languagesLine(ctx: TemplateContext): string | null {
  const languages = ctx.doc.brief?.languages ?? [];
  if (!languages.length) return null;
  return capFirst(
    languages
      .map((l, i) => (l.platforms.length ? `${l.language} on ${joinAnd(l.platforms.map(platformName))}` : i === 0 && languages.length > 1 ? `${l.language} first` : l.language))
      .join(", "),
  );
}

export function BriefSlide({ ctx }: { ctx: TemplateContext }) {
  const brief = ctx.doc.brief!;
  const t = slideText(ctx);
  const recap = brief.recap;
  // One highlighted line: no line breaks, no highlight marks inside it. A
  // headline written before brief.headline existed (the slide's lines) still
  // shows, without its own "The brief:".
  const briefHeadline = (brief.headline ?? t.headline ?? "")
    .replace(/\\+n/g, " ")
    .replace(/^\s*The brief:\s*/i, "")
    .replace(/[*\n]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const business = brief.business_objective;
  const social = brief.social_objective;
  const markets = brief.markets.map((m) => m.name || countryName(m.country));
  const approvers = [...brief.approvers].sort((a, b) => (a.role === b.role ? 0 : a.role === "agency" ? -1 : 1));
  const budget = brief.budget;
  const budgetText = budget && (budget.content?.value != null || budget.creators?.value != null)
    ? [budget.content?.value != null ? `${num(budget.content)} for content` : null, budget.creators?.value != null ? `${num(budget.creators)} for creators` : null].filter(Boolean).join(", ")
    : null;
  const who = recap ? [recap.person && recap.organisation ? `${recap.person}, ${recap.organisation}` : recap.person ?? recap.organisation, recap.date ? `${recap.occasion ?? "the request"} of ${dayLongYear(recap.date)}` : recap.occasion].filter(Boolean).join(" · ") : "";
  const facts: Array<{ key: string; value: ReactNode }> = [
    { key: "Period", value: ctx.periodStart && ctx.periodEnd ? <V>{monthYear(ctx.periodStart)} to {monthYear(ctx.periodEnd)}</V> : null },
    { key: "Markets", value: markets.length ? <V>{joinAnd(markets)}</V> : null },
    { key: "Languages", value: languagesLine(ctx) ? <V>{languagesLine(ctx)}</V> : null },
    { key: "Budget", value: budgetText ? <V>{budgetText}</V> : null },
    { key: "Approves", value: approvers.length ? <V>{approvers.map((a) => (a.organisation ? `${a.name} at ${a.organisation}` : a.name)).join(", then ")}</V> : null },
  ];
  const objective = (key: string, figureNode: ReactNode, line: ReactNode, source?: StrategySource | null) => (
    <div className="sv-brief-objective">
      <Key>{key}</Key>
      <p className="sv-brief-figure">{figureNode}</p>
      {line ? <p className="sv-brief-line">{line}</p> : null}
      {source?.label || source?.kind === "open" ? <p className="sv-open sv-brief-source"><V>{source.label ?? source.what_would_settle_it}</V></p> : null}
    </div>
  );
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        {/* Template 41: the fixed "The brief:" and the strategist's brief.headline in the highlight. */}
        <Headline text={briefHeadline ? `The brief:\n*${briefHeadline}*` : null} fallback="The brief." />
        {recap ? (
          <div className="sv-brief-quote">
            <div className="sv-brief-quote-row">
              <span aria-hidden className="sv-brief-mark" />
              <V as="p" dir="auto">“{recap.text.replace(/^[“"]|[”"]$/g, "")}”</V>
            </div>
            {who ? <V as="p" className="sv-brief-who">{who}</V> : null}
          </div>
        ) : null}
        <div className="sv-brief-objectives">
          {objective("The business objective", <FigureOr value={num(business?.number, { lower: true })} />, business?.line ? <V>{business.line}</V> : null, business?.source)}
          {objective("The objective on social", <FigureOr value={num(social?.number, { lower: true })} />, social ? <V>{social.number_line ?? social.text}</V> : null, social?.source)}
        </div>
        <span className="sv-push" />
        <div className="sv-brief-facts">
          {facts.map((fact) => (
            <div key={fact.key} className="sv-brief-fact">
              <Key>{fact.key}</Key>
              <div>{fact.value ?? <span className="sv-open">To confirm</span>}</div>
            </div>
          ))}
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}
